import { PATH_AUTH } from '@/routes/paths';
import type { StoreState } from '@/store/use-store';
import type { CreateUserRequest, User, UserSliceType, UserState } from '@/types/user';
import i18n from 'i18next';
import getAxiosConnection from '../utils/axios';

interface LoginResponse {
  token: string;
  refresh_token: string;
}

const initialState: UserState = {
  user: null,
  authenticating: false,
  authenticated: false,
  deleting: false,
  loadingUser: false,
  loadingUsers: false,
  loadingQuarterlyFuelConsumptions: false,
  loggedOut: false,
  quarterlyFuelConsumptions: [],
  registering: false,
  updatingThirdPartyData: false,
  registered: false,
  resetting: false,
  reset: false,
  savingUser: false,
  users: [],
  verifying: false,
  impersonatedUser: ''
};

type SliceCreator = (
  set: (
    partial:
      | UserState
      | Partial<UserState>
      | ((state: UserState) => UserState | Partial<UserState>),
    replace?: boolean
  ) => void,
  get: () => StoreState
) => UserSliceType;

const userSlice: SliceCreator = (set, get) => ({
  ...initialState,
  createUser: async (user: CreateUserRequest) => {
    set({ savingUser: true });
    return getAxiosConnection()
      .post('users', { ...user })
      .then((res) => {
        let { users } = get();
        const addedUser: User = res.data;
        //get current logged in user
        const currentLoggedInUser = get().user;
        users = users.filter((u) => u.id !== addedUser.id);
        users.push(addedUser);
        // Uitgenodigde user registreert zichzelf, zet deze als ingelogde user.
        if (currentLoggedInUser.id === addedUser.id) {
          addedUser.token = user.token;
          addedUser.refreshToken = user.refreshToken;
          set({ user: addedUser });
        }
        set({ users });
      })
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {
        set({ savingUser: false });
      });
  },
  getUserDeletionImpact: async (id: number) =>
    getAxiosConnection()
      .get(`user/${id}/deletion-impact`)
      .then((res) => res.data)
      .catch((e) => {
        throw e;
      }),
  deleteUser: async (id: number) => {
    set({ deleting: true });
    return getAxiosConnection()
      .get(`user/delete/${id}`)
      .then(() => {
        const users = get().users.filter((user) => user.id !== id);
        set({ users });
      })
      .catch((e) => {
        throw e;
      })
      .finally(() => {
        set({ deleting: false });
      });
  },
  getUser: async (id: number) => {
    set({ loadingUser: true });
    return getAxiosConnection()
      .get(`user/get/${id}`)
      .then((res) => res.data)
      .catch((e) => {
        console.log(e);
        throw e;
      })
      .finally(() => {
        set({ loadingUser: false });
      });
  },
  login: async (username: string, password: string, rememberMe: boolean) => {
    set({ authenticating: true });
    const user = {
      username,
      password,
      remember: rememberMe
    };
    return getAxiosConnection()
      .post<LoginResponse>('login', { ...user })
      .then((res) => {
        const authenticatedUser = {
          ...get().user,
          token: res.data.token,
          refreshToken: res.data.refresh_token
        };
        set({
          authenticating: false,
          loggedOut: false,
          authenticated: true,
          user: authenticatedUser
        });
        get().getUserInfo();
      })
      .catch((e) => {
        set({ authenticating: false });
        throw e;
      });
  },
  getUsers: async (refresh = false) => {
    const { users } = get();
    if (!refresh && users.length > 0) {
      return;
    }
    set({ loadingUsers: true });
    getAxiosConnection()
      .get<User[]>('users')
      .then((res) => {
        set({ users: res.data });
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        set({ loadingUsers: false });
      });
  },
  getUserInfo: async () => {
    set({ loadingUser: true });
    return getAxiosConnection()
      .get<User>('user/current')
      .then((res) => {
        const authenticatedUser = {
          ...get().user,
          ...res.data
        };
        set({
          user: authenticatedUser
        });

        i18n.changeLanguage(authenticatedUser.language);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        set({ loadingUser: false });
      });
  },
  getQuarterlyFuelConsumptions: async () => {
    set({ loadingQuarterlyFuelConsumptions: true });
    return getAxiosConnection()
      .get('/user/quarterlyFuelConsumptions')
      .then((res) => {
        set({ quarterlyFuelConsumptions: res.data });
        return res.data;
      })
      .catch((e) => {
        console.log(e);
        return [];
      })
      .finally(() => {
        set({ loadingQuarterlyFuelConsumptions: false });
      });
  },
  passwordReset: async (email: string) => {
    set({ authenticating: true });
    const user = { email };
    getAxiosConnection()
      .post('forgotpassword', { ...user, url: import.meta.env.VITE_APP_URL + PATH_AUTH.verify })
      .then(() => {
        set({
          resetting: false,
          reset: true
        });
      })
      .catch(() => {
        console.log('error');
        set({ resetting: false });
      });
  },
  setPassword: async (token: string, password: string) => {
    set({ verifying: true });
    return getAxiosConnection()
      .post('setpassword', { token, password })
      .then(() => 'success')
      .catch((e) => e)
      .finally(() => {
        set({
          verifying: false
        });
      });
  },
  logout: () => {
    set(() => ({
      ...initialState,
      loggedOut: true
    }));
  },
  register: async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    roles: string[],
    publicData: boolean,
    certificatesArePublic = false,
    allowedThirdParties = [],
    invitationUUID = ''
  ) => {
    set({ registering: true });
    const { language } = i18n;
    const user = {
      email,
      password,
      firstName,
      lastName,
      roles,
      public: publicData,
      language,
      invitationUUID,
      allowedThirdParties,
      certificatesArePublic
    };
    return getAxiosConnection()
      .post('register', { ...user })
      .then(() => {
        set({
          registering: false,
          registered: true
        });
        get().refresh();
      })
      .catch((e) => {
        set({ registering: false });
        throw e;
      });
  },
  refresh: () => {
    set(() => ({
      ...initialState,
      loggedOut: false
    }));
  },
  impersonateUser: async (userEmail: string) => {
    set({ impersonatedUser: userEmail });
    return get().getUserInfo();
  },
  updateThirdPartyData: async (user) => {
    set({ updatingThirdPartyData: true });
    return getAxiosConnection()
      .patch('update-third-party-settings', { ...user })
      .then((res) => {
        set({
          updatingThirdPartyData: false
        });
        const authenticatedUser = {
          ...get().user,
          ...res.data
        };
        set({
          user: authenticatedUser
        });
      })
      .catch((e) => {
        set({ updatingThirdPartyData: false });
        throw e;
      });
  },
  promoteAdminUser: async (id) =>
    getAxiosConnection()
      .get(`/user/${id}/promote`)
      .then(() => get().getUsers(true))
      .catch((e) => {
        throw e;
      }),
  demoteAdminUser: async (id: number) =>
    getAxiosConnection()
      .get(`/user/${id}/demote`)
      .then(() => get().getUsers(true))
      .catch((e) => {
        throw e;
      }),
  verifyEmail: async (token: string) =>
    getAxiosConnection()
      .get(`verify-email/${token}`)
      .then((res) => res.data)
      .catch((e) => {
        throw e;
      }),
  approveUser: async (id: number) =>
    getAxiosConnection()
      .get(`/user/${id}/approve`)
      .then(() => get().getUsers(true))
      .catch((e) => {
        throw e;
      })
});

export default userSlice;
